/**
 * AdminModeBar — red, fixed-top impersonation banner.
 *
 * Visibility contract:
 *   - user.is_impersonating === true  → render
 *   - any other case                  → render null
 *
 * Features:
 *   - Live countdown via `useImpersonationCountdown` against
 *     `user.impersonation.expires_at`.
 *       * > 5 min: "53m remaining" (whole-minute updates)
 *       * ≤ 5 min, ≥ 1 min: "4m 32s" (per-second)
 *       * < 1 min: `animate-pulse` + "expires in <1m"
 *   - One-shot 55-min soft warning toast at T-5 min — idempotent
 *     across refocus/tab change via a ref inside the hook.
 *   - Hard auto-exit at T-0 — POST /api/admin/impersonation/exit,
 *     refresh /auth/me, navigate to /admin, toast "Impersonation
 *     session expired (1 hour limit)".
 *   - Manual "Exit" button — POST exit, refresh, navigate to
 *     /admin/accounts/{exitedTargetId} so the admin lands back on
 *     the user they were just acting as.
 *   - Mobile (≤md): 2-row stack at 72px height (export
 *     `ADMIN_MODE_BAR_HEIGHT_MOBILE_PX = 72` for consumers that
 *     reserve viewport space).
 *   - Mutually exclusive with the orange `<AdminViewBar>` — that
 *     component returns null when `is_impersonating === true`.
 */
import React, { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import useImpersonationCountdown, {
  formatImpersonationRemaining,
  FIVE_MINUTE_THRESHOLD_SECONDS,
} from "@/hooks/useImpersonationCountdown";

export const ADMIN_MODE_BAR_HEIGHT_PX = 36;
export const ADMIN_MODE_BAR_HEIGHT_MOBILE_PX = 72;

const EXIT_TIMEOUT_TOAST =
  "Impersonation session expired (1 hour limit)";
const FIVE_MINUTE_WARNING_TOAST =
  "Impersonation session expires in 5 minutes";

export default function AdminModeBar() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();

  // ---- ALL HOOKS MUST RUN UNCONDITIONALLY (rules of hooks) -----------
  // We compute `is_impersonating` + extract the sidecar BEFORE any
  // early returns, so the hook order is stable across renders.
  const isImpersonating = !!user?.is_impersonating;
  const sidecar = user?.impersonation || null;
  const targetId = user?.id; // during impersonation, `user` IS the target
  const expiresAt = sidecar?.expires_at || null;

  const exitingRef = useRef(false);
  // Phase 4b — banner height publisher.
  // The bar is `fixed top-0`, so downstream shells (Admin/Customer/Provider)
  // would collide with page content without an offset. We publish the
  // banner's live `offsetHeight` as `--admin-mode-bar-h` on the document
  // root; each shell consumes it as `paddingTop: var(--admin-mode-bar-h, 0px)`.
  //
  // Why this pattern (not a hardcoded pixel offset):
  //   - Banner height varies by breakpoint (72px mobile / 36px desktop today).
  //   - It can grow further if the impersonated email wraps or a future
  //     status pill stacks — ResizeObserver keeps the offset accurate.
  //   - When NOT impersonating we REMOVE the CSS var entirely, so the
  //     shells' fallback `0px` kicks in and there is zero residual reflow.
  //
  // Regression note: this fix reinstates the offset originally lost in
  // commit 47ae78b (App Shell trilogy) which dropped the historic `pt-9`
  // AppShell padding on the (then-correct) assumption that the bar was
  // still flag-gated. Backlog D (4191611) shipped the flag 20h later —
  // unmasking the overlap on all three new shells.
  const barRef = useRef(null);

  const exit = useCallback(
    async ({ navigateTo, toastSuccess, toastError }) => {
      if (exitingRef.current) return;
      exitingRef.current = true;
      try {
        await api.post("/admin/impersonation/exit");
        await refreshMe();
        if (toastSuccess) toast.success(toastSuccess);
        navigate(navigateTo);
      } catch (e) {
        // Best-effort — even on error, refresh state and bounce home.
        try {
          await refreshMe();
        } catch {
          /* ignore */
        }
        const msg =
          formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Failed to exit impersonation";
        toast.error(toastError || msg);
        navigate("/admin");
      } finally {
        exitingRef.current = false;
      }
    },
    [navigate, refreshMe]
  );

  const onFiveMinuteWarning = useCallback(() => {
    toast(FIVE_MINUTE_WARNING_TOAST, {
      duration: 8000,
      // Sonner doesn't natively support data-testid on the toast itself,
      // so we surface this aria-label on the description for E2E tests
      // that target the toast container.
      description: undefined,
      id: "admin-mode-bar-five-min-warning-toast",
    });
  }, []);

  const onExpire = useCallback(() => {
    // Auto-exit, refresh, navigate to /admin, toast the timeout.
    exit({
      navigateTo: "/admin",
      toastSuccess: EXIT_TIMEOUT_TOAST,
      toastError: EXIT_TIMEOUT_TOAST,
    });
  }, [exit]);

  const remainingSeconds = useImpersonationCountdown({
    expiresAt,
    onFiveMinuteWarning,
    onExpire,
  });

  // Phase 4b — publish live banner height (or clear the var when the
  // bar is hidden). `useLayoutEffect` so downstream shells see the
  // right offset on the FIRST paint — no visible content jump.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!isImpersonating || !barRef.current) {
      root.style.removeProperty("--admin-mode-bar-h");
      return undefined;
    }
    const el = barRef.current;
    const publish = () => {
      root.style.setProperty(
        "--admin-mode-bar-h",
        `${el.offsetHeight}px`,
      );
    };
    publish();
    // ResizeObserver keeps the offset accurate through breakpoint
    // flips (72px ↔ 36px) and any future wrap-driven growth.
    const RO = typeof ResizeObserver !== "undefined" ? ResizeObserver : null;
    let observer = null;
    if (RO) {
      observer = new RO(publish);
      observer.observe(el);
    }
    return () => {
      if (observer) observer.disconnect();
      root.style.removeProperty("--admin-mode-bar-h");
    };
  }, [isImpersonating]);

  // ---- Now the early-return is safe — all hooks already ran ----------
  if (!isImpersonating) return null;

  const isUnderFiveMin =
    remainingSeconds != null &&
    remainingSeconds <= FIVE_MINUTE_THRESHOLD_SECONDS;
  const isUnderOneMin =
    remainingSeconds != null && remainingSeconds < 60;

  const countdownLabel = formatImpersonationRemaining(remainingSeconds);
  const targetEmail = user?.email;
  const adminEmail = sidecar?.admin_email;

  const handleManualExit = () => {
    // exitedTargetId may be the customer/provider we were acting as —
    // navigate back to their account detail page so the admin can
    // continue support work without re-searching.
    const exitedTargetId = targetId;
    exit({
      navigateTo: exitedTargetId
        ? `/admin/accounts/${exitedTargetId}`
        : "/admin",
      toastSuccess: "Returned to admin",
    });
  };

  return (
    <div
      ref={barRef}
      role="status"
      aria-label="Impersonation active"
      data-testid="admin-mode-bar"
      className={[
        "fixed inset-x-0 top-0 z-50 bg-red-600 text-white shadow-md",
        // Mobile: 2-row stack at 72px. Desktop (md+): single 36px row.
        "h-[72px] md:h-9",
        isUnderOneMin && "animate-pulse",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Mobile 2-row stack */}
      <div className="flex h-full flex-col items-stretch justify-between px-3 py-1.5 md:hidden">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold leading-tight">
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span
            className="truncate"
            data-testid="admin-mode-bar-text-mobile"
          >
            Acting as{" "}
            <span
              className="font-bold"
              data-testid="admin-mode-bar-email"
            >
              {targetEmail}
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs">
          <span
            className="font-semibold tabular-nums tracking-tight"
            data-testid="admin-mode-bar-countdown"
          >
            {countdownLabel}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleManualExit}
            className="h-7 rounded-full border-white bg-transparent px-3 text-xs font-semibold text-white hover:bg-white hover:text-red-600"
            data-testid="admin-mode-bar-exit-btn"
          >
            <LogOut className="mr-1 h-3 w-3" />
            Exit
          </Button>
        </div>
      </div>

      {/* Desktop single-row */}
      <div className="hidden h-full items-center justify-center gap-3 px-6 text-sm font-semibold tracking-wide md:flex">
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span data-testid="admin-mode-bar-text">
          IMPERSONATING —{" "}
          <span data-testid="admin-mode-bar-email">{targetEmail}</span>
          {adminEmail && (
            <span className="ml-1 font-normal text-red-100">
              · admin: {adminEmail}
            </span>
          )}
        </span>
        <span
          className={[
            "ml-2 inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs tabular-nums",
            isUnderFiveMin && "bg-white/25 font-bold",
          ]
            .filter(Boolean)
            .join(" ")}
          data-testid="admin-mode-bar-countdown"
        >
          {countdownLabel}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleManualExit}
          disabled={exitingRef.current}
          className="ml-2 h-7 rounded-full border-white bg-transparent px-3 text-xs font-semibold text-white hover:bg-white hover:text-red-600"
          data-testid="admin-mode-bar-exit-btn"
        >
          {exitingRef.current ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <LogOut className="mr-1 h-3 w-3" />
          )}
          Exit
        </Button>
      </div>
    </div>
  );
}
